import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { clearAccidentDashboard, getExpiredAppointments } from "../../../../actions/accidentDashboard";
import { clearForm } from "../../../../actions/forms";
import { setFormsGeneralProperties } from "../../../../actions/formsState";
import { getConversations } from "../../../../actions/messaging";
import { formBehaviourEnum } from "../../../commons/enum/formEnums";
import { StyledAccidentDashboard } from "../../../styled/accident";
import { StyledFlex } from "../../../styled/flex";
import FormContainer from "../../forms/formContainer";
import AddDocumentation from "./addDocumentation/addDocumentation";
import AppointmentsCalendar from "./appointmentsCalendar/appointmentsCalendar";
import AttentionsInAnotherProvider from "./attentionsInAnotherProvider/attentionsInAnotherProvider";
import AccidentDocuments from "./documents/accidentDocuments";
import ExpiredAppointmentsDialog from "./expiredAppointments/expiredAppointmentsDialog";
import AccidentHeader from "./header/accidentHeader";
import BackArrowHeader from "./header/backArrowHeader";
import AccidentMessaging from "./messaging/accidentMessaging";
import PendingMessageAlertDialog from "./messaging/pendingMessageAlertDialog";
import AccidentForms from "./prePrintedForms/accidentPrePrintedForms";
import { shouldShowPendingMessageAlert } from "../../../../actions/messaging";

const AccidentDashboard = ({ setDisplayAccidents }) => {
  const dispatch = useDispatch();
  const accident = useSelector((state) => state.accidentDashboard.accident);
  const formStateBehaviour = useSelector((state) => state.formsState.behaviour);
  const expiredAppointments = useSelector((state) => state.accidentDashboard.expiredUninformedAppointments);
  const roles = useSelector((state) => state.auth.roles);
  const providerId = useSelector((state) => state.providers.selectedProvider);

  const conversations = useSelector((state) => state.messaging.conversations.data);
  const isLoadingConversations = useSelector((state) => state.messaging.conversations.isLoading);

  const [expiredAppointmentsDialogOpen, setExpiredAppointmentsDialogOpen] = useState(false);
  const [hasCheckedExpiredAppointments, setHasCheckedExpiredAppointments] = useState(false);
  const [pendingMessageAlertOpen, setPendingMessageAlertOpen] = useState(false);
  const [hasCheckedPendingMessageAlert, setHasCheckedPendingMessageAlert] = useState(false);

  const onBackClick = () => {
    setDisplayAccidents("block");
  };

  useEffect(() => {
    if (formStateBehaviour !== formBehaviourEnum.ATTENTION_IN_OTHER_PROVIDER) {
      dispatch(
        setFormsGeneralProperties({
          idSiniestro: accident.idSiniestro,
          idArt: accident.mlCloudCoreArtId,
          artName: accident.art,
          documento: accident.documento,
          trabajador: accident.trabajador ?? accident.apellidoTrabajador + ' ' + accident.nombreTrabajador,
          apellidoTrabajador: accident.apellidoTrabajador,
          nombreTrabajador: accident.nombreTrabajador,
          numeroHistoriaClinica: accident.siniestro,
        }),
      );
    }
  }, [
    accident.idSiniestro,
    accident.mlCloudCoreArtId,
    accident.art,
    formStateBehaviour,
  ]);

  useEffect(() => {
    return () => {
      dispatch(clearAccidentDashboard());
      dispatch(clearForm());
    };
  }, []);

  useEffect(() => {
    if (roles.PRESTADOR) dispatch(getExpiredAppointments(accident.idSiniestro, accident.mlCloudCoreArtId));
  }, [accident.idSiniestro, accident.mlCloudCoreArtId, dispatch, providerId]);

  useEffect(() => {
    if (accident.mensajeriaActivada && accident?.idSiniestro && accident?.mlCloudCoreArtId) {
      Promise.all([
        dispatch(getConversations(accident.idSiniestro, accident.mlCloudCoreArtId, providerId)),
        dispatch(shouldShowPendingMessageAlert(accident.mlCloudCoreArtId))
      ]).then(([conversationsRes, alertRes]) => {
        const convs = conversationsRes?.value?.data || [];
        const shouldShow = alertRes?.value === true || alertRes?.payload === true;
        
        if (shouldShow && !hasCheckedPendingMessageAlert) {
          const hasPending = convs.some(c => c.mensajesPendientes > 0);
          if (hasPending) {
            setPendingMessageAlertOpen(true);
            setHasCheckedPendingMessageAlert(true);
          }
        }
      }).catch(() => {});
    }
  }, [accident.idSiniestro, accident.mlCloudCoreArtId, dispatch, providerId, hasCheckedPendingMessageAlert]);

  useEffect(() => {
    if (expiredAppointments.data.length > 0 && !expiredAppointments.isFetching && !hasCheckedExpiredAppointments) {
      setExpiredAppointmentsDialogOpen(true);
      setHasCheckedExpiredAppointments(true);
    }
  }, [expiredAppointments.isFetching, expiredAppointments.data, hasCheckedExpiredAppointments]);

  const handleCloseExpiredAppointmentsDialog = () => {
    setExpiredAppointmentsDialogOpen(false);
    setHasCheckedExpiredAppointments(true);
  };

  return (
    <>
      <FormContainer />

      <StyledFlex.ColumnContainer>
        <StyledAccidentDashboard.RowContainer>
          <BackArrowHeader onClick={onBackClick} />
          <StyledAccidentDashboard.HeaderDiv elevation={2}>
            <AccidentHeader />
          </StyledAccidentDashboard.HeaderDiv>
        </StyledAccidentDashboard.RowContainer>
        <StyledAccidentDashboard.RowContainer>
          <StyledAccidentDashboard.AddDocumentationDiv elevation={2}>
            <AddDocumentation />
          </StyledAccidentDashboard.AddDocumentationDiv>
          <StyledAccidentDashboard.AccidentDocumentsDiv elevation={2}>
            <AccidentDocuments
              idSiniestro={accident.idSiniestro}
              idArt={accident.mlCloudCoreArtId}
            />
          </StyledAccidentDashboard.AccidentDocumentsDiv>
        </StyledAccidentDashboard.RowContainer>
        <StyledAccidentDashboard.RowContainer>
          <StyledAccidentDashboard.FormAndAppointmentsContainer>
            <StyledAccidentDashboard.FormsDiv elevation={2}>
              <AccidentForms />
            </StyledAccidentDashboard.FormsDiv>
            <StyledAccidentDashboard.AppointmentsInProviderDiv elevation={2}>
              <AttentionsInAnotherProvider />
            </StyledAccidentDashboard.AppointmentsInProviderDiv>
          </StyledAccidentDashboard.FormAndAppointmentsContainer>
          <StyledAccidentDashboard.CalendarDiv>
            <AppointmentsCalendar />
          </StyledAccidentDashboard.CalendarDiv>
        </StyledAccidentDashboard.RowContainer>
        {accident.mensajeriaActivada && (
          <StyledAccidentDashboard.RowContainer>
            <StyledAccidentDashboard.MessagingDiv elevation={2}>
              <AccidentMessaging conversations={conversations} isLoading={isLoadingConversations} />
            </StyledAccidentDashboard.MessagingDiv>
          </StyledAccidentDashboard.RowContainer>
        )}
      </StyledFlex.ColumnContainer>
      {roles.PRESTADOR && <ExpiredAppointmentsDialog
        open={expiredAppointmentsDialogOpen}
        handleClose={handleCloseExpiredAppointmentsDialog}
        accident={accident}
        appointments={expiredAppointments.data || []}
      />}
      <PendingMessageAlertDialog 
        open={pendingMessageAlertOpen}
        handleClose={() => setPendingMessageAlertOpen(false)}
      />
    </>
  );
};

export default AccidentDashboard;
